import React, {useState, useEffect} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Icon from './icons/Icon';
import MiaPlatformDataService from '../services/MiaPlatformDataService';
import providerLinkService from '../auth/ProviderLinkService';
import './CreateServiceModal.css';

const GIT_PROVIDERS = [
  { value: 'github', label: 'GitHub', providerAlias: 'github' },
  { value: 'gitlab', label: 'GitLab', providerAlias: 'gitlab' },
  { value: 'azure', label: 'Azure DevOps', providerAlias: 'azure-devops' },
];

/**
 * Create Service Modal Component
 * Modal for creating a new service from Mia Flow templates
 */
const CreateServiceModal = ({
  isOpen,
  onClose,
  onCreate,
  projectId,
  revisionId,
  tenantId
}) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('template');

  // Common fields
  const [serviceName, setServiceName] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [error, setError] = useState(null);

  // Template tab state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Git import tab state
  const [gitProvider, setGitProvider] = useState('');
  const [repositoryUrl, setRepositoryUrl] = useState('');
  const [dockerImage, setDockerImage] = useState('');
  const [connectedProviders, setConnectedProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);

  // Load templates when modal opens and template tab is active
  useEffect(() => {
    if (isOpen && tenantId && activeTab === 'template') {
      loadTemplates();
    }
  }, [isOpen, tenantId, activeTab]);

  // Load connected git providers when git tab becomes active
  useEffect(() => {
    if (isOpen && activeTab === 'git') {
      loadConnectedProviders();
    }
  }, [isOpen, activeTab]);

  // Auto-update docker image when service name changes (only if user hasn't typed anything)
  useEffect(() => {
    if (activeTab === 'git' && serviceName && !dockerImage) {
      const autoImage = `${serviceName.toLowerCase().trim().replace(/\s+/g, '-')}:latest`;
      setDockerImage(autoImage);
    }
  }, [serviceName, activeTab]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('template');
      setServiceName('');
      setServiceDescription('');
      setSelectedTemplate(null);
      setSearchQuery('');
      setGitProvider('');
      setRepositoryUrl('');
      setDockerImage('');
      setConnectedProviders([]);
      setError(null);
    }
  }, [isOpen]);

  const loadConnectedProviders = async () => {
    setProvidersLoading(true);
    try {
      const results = await Promise.all(
        GIT_PROVIDERS.map(p =>
          providerLinkService.checkLinkStatus(p.providerAlias)
            .then(status => ({ ...p, connected: status.linked }))
            .catch(() => ({ ...p, connected: false }))
        )
      );
      const connected = results.filter(p => p.connected);
      setConnectedProviders(connected);
      if (connected.length > 0 && !gitProvider) {
        setGitProvider(connected[0].value);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading connected providers:', err);
    } finally {
      setProvidersLoading(false);
    }
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    setError(null);
    try {
      const result = await MiaPlatformDataService.getMiaFlowTemplates(tenantId);
      if (result.success) {
        // Sort templates alphabetically by name
        const sortedTemplates = (result.data.items || []).sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        setTemplates(sortedTemplates);
      } else {
        setError(result.error || 'Failed to load templates');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!serviceName.trim()) {
      setError('Service name is required');
      return;
    }
    if (!selectedTemplate) {
      setError('Please select a template');
      return;
    }

    setCreateLoading(true);
    setError(null);
    try {
      const result = await MiaPlatformDataService.createServiceFromTemplate({
        projectId,
        revisionId,
        serviceName: serviceName.trim(),
        serviceDescription: serviceDescription.trim(),
        templateId: selectedTemplate.id,
        templateItemId: selectedTemplate.itemId,
        templateTenantId: selectedTemplate.tenantId,
        templateVersion: selectedTemplate.version?.name || selectedTemplate.version,
      });

      if (result.success) {
        if (onCreate) {
          onCreate(result.data);
        }
        onClose();
      } else {
        // Show the actual error message from the server
        const errorMsg = result.error || 'Failed to create service';
        setError(errorMsg);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error creating service:', err);
      // Show the actual error message if available
      const errorMsg = err.message || 'Failed to create service';
      setError(errorMsg);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleGitImport = async () => {
    if (!serviceName.trim()) {
      setError('Service name is required');
      return;
    }
    if (!repositoryUrl.trim()) {
      setError('Repository URL is required');
      return;
    }

    // Basic URL validation
    try {
      new URL(repositoryUrl);
    } catch (e) {
      setError('Please enter a valid repository URL');
      return;
    }

    setCreateLoading(true);
    setError(null);
    try {
      const result = await MiaPlatformDataService.createServiceFromGit({
        projectId,
        revisionId,
        serviceName: serviceName.trim(),
        serviceDescription: serviceDescription.trim(),
        gitProvider,
        repositoryUrl: repositoryUrl.trim(),
        ...(dockerImage.trim() ? { dockerImage: dockerImage.trim() } : {}),
      });

      if (result.success) {
        if (onCreate) {
          onCreate(result.data);
        }
        onClose();
      } else {
        const errorMsg = result.error || 'Failed to create service from Git';
        setError(errorMsg);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error creating service from Git:', err);
      const errorMsg = err.message || 'Failed to create service from Git';
      setError(errorMsg);
    } finally {
      setCreateLoading(false);
    }
  };

  const getRepositoryPlaceholder = () => {
    switch (gitProvider) {
      case 'github':
        return 'https://github.com/owner/repository';
      case 'gitlab':
        return 'https://gitlab.com/group/project';
      case 'azure':
        return 'https://dev.azure.com/organization/project/_git/repository';
      case 'bitbucket':
        return 'https://bitbucket.org/workspace/repository';
      default:
        return 'Repository URL';
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-service-modal" onClick={(e) => e.stopPropagation()}>
        {createLoading && (
          <div className="hexagonal-loader-container">
            <div className="hexagonal-loader">
              <div className="hexagon"></div>
              <div className="hexagon"></div>
              <div className="hexagon"></div>
              <div className="hexagon"></div>
              <div className="hexagon"></div>
              <div className="hexagon"></div>
            </div>
          </div>
        )}
        <div className="modal-header">
          <h2>Create New Service</h2>
          <button 
            className="modal-close-btn"
            onClick={onClose}
            disabled={createLoading}
            type="button"
          >
            <Icon name="x" size={24} />
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              <Icon name="alert-circle" size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="tabs-navigation">
            <button
              className={`tab-button ${activeTab === 'template' ? 'active' : ''}`}
              onClick={() => setActiveTab('template')}
              disabled={createLoading}
              type="button"
            >
              <Icon name="box" size={16} />
              <span>Create from Template</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'git' ? 'active' : ''}`}
              onClick={() => setActiveTab('git')}
              disabled={createLoading}
              type="button"
            >
              <Icon name="git-branch" size={16} />
              <span>Import from Git</span>
            </button>
          </div>

          {/* Template Tab Content */}
          {activeTab === 'template' && (
            <>
              <div className="form-row-inline">
                <div className="form-group form-group-name">
                  <label htmlFor="serviceName">Service Name *</label>
                  <input
                    id="serviceName"
                    type="text"
                    value={serviceName}
                    onChange={(e) => setServiceName(e.target.value)}
                    placeholder="my-service"
                    disabled={createLoading}
                  />
                </div>
                <div className="form-group form-group-description">
                  <label htmlFor="serviceDescription">Description</label>
                  <input
                    id="serviceDescription"
                    type="text"
                    value={serviceDescription}
                    onChange={(e) => setServiceDescription(e.target.value)}
                    placeholder="Service description..."
                    disabled={createLoading}
                  />
                </div>
              </div>

              <div className="form-group templates-section">
                <div className="templates-header">
                  <label>Template *</label>
                  {templates.length > 0 && !templatesLoading && (
                    <div className="search-input-wrapper">
                      <Icon name="search" size={16} />
                      <input
                        type="text"
                        className="template-search-input"
                        placeholder="Search templates..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        disabled={createLoading}
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="clear-search-btn"
                          onClick={() => setSearchQuery('')}
                          disabled={createLoading}
                        >
                          <Icon name="x" size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {templatesLoading ? (
                  <div className="loading-state-inline">
                    <Icon name="spinner" size={20} />
                    <span>Loading templates...</span>
                  </div>
                ) : templates.length > 0 ? (
                  <div className="templates-grid">
                    {templates
                      .filter(template => 
                        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (template.description || '').toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .map((template) => (
                      <button
                        key={template.id}
                        className={`template-card ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                        onClick={() => setSelectedTemplate(template)}
                        type="button"
                        disabled={createLoading}
                      >
                        <div className="template-card-header">
                          {template.imageUrl ? (
                            <img 
                              src={template.imageUrl} 
                              alt={template.name}
                              className="template-logo"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className="template-icon-fallback" style={{display: template.imageUrl ? 'none' : 'flex'}}>
                            <Icon name="box" size={32} />
                          </div>
                        </div>
                        <div className="template-card-body">
                          <h3 className="template-card-name">{template.name}</h3>
                          <p className="template-card-description">
                            {template.description || 'No description available'}
                          </p>
                          <div className="template-card-footer">
                            <span className="template-version">v{template.version?.name || template.version}</span>
                            {selectedTemplate?.id === template.id && (
                              <Icon name="check-circle" size={18} className="selected-icon" />
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state-inline">
                    <p>No Mia Flow templates found</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Git Import Tab Content */}
          {activeTab === 'git' && (
            <>
              <div className="form-group">
                <label htmlFor="gitProvider">Git Provider *</label>
                {providersLoading ? (
                  <div className="loading-state-inline">
                    <Icon name="spinner" size={16} />
                    <span>Loading connected providers...</span>
                  </div>
                ) : connectedProviders.length === 0 ? (
                  <div className="empty-state-inline">
                    <p>No git providers connected. Go to <strong>Connectors</strong> to link your account.</p>
                  </div>
                ) : (
                  <select
                    id="gitProvider"
                    value={gitProvider}
                    onChange={(e) => setGitProvider(e.target.value)}
                    disabled={createLoading}
                  >
                    {connectedProviders.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="repositoryUrl">Repository URL *</label>
                <input
                  id="repositoryUrl"
                  type="text"
                  value={repositoryUrl}
                  onChange={(e) => setRepositoryUrl(e.target.value)}
                  placeholder={getRepositoryPlaceholder()}
                  disabled={createLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="gitServiceName">Service Name *</label>
                <input
                  id="gitServiceName"
                  type="text"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  placeholder="my-service"
                  disabled={createLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="gitServiceDescription">Description</label>
                <textarea
                  id="gitServiceDescription"
                  value={serviceDescription}
                  onChange={(e) => setServiceDescription(e.target.value)}
                  placeholder="Service description..."
                  rows={3}
                  disabled={createLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="dockerImage">Docker Image</label>
                <input
                  id="dockerImage"
                  type="text"
                  value={dockerImage}
                  onChange={(e) => setDockerImage(e.target.value)}
                  placeholder="my-service:latest"
                  disabled={createLoading}
                />
                <small className="form-help">
                  Docker image name (optional, auto-generated from service name if left empty)
                </small>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={createLoading}
            type="button"
          >
            Cancel
          </button>
          {activeTab === 'template' ? (
            <button
              className="btn-primary"
              onClick={handleCreate}
              disabled={createLoading || !serviceName.trim() || !selectedTemplate}
              type="button"
            >
              {createLoading ? (
                <>
                  <Icon name="spinner" size={18} />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Icon name="plus" size={18} />
                  <span>Create Service</span>
                </>
              )}
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={handleGitImport}
              disabled={createLoading || !serviceName.trim() || !repositoryUrl.trim() || !gitProvider || connectedProviders.length === 0}
              type="button"
            >
              {createLoading ? (
                <>
                  <Icon name="spinner" size={18} />
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Icon name="git-branch" size={18} />
                  <span>Import Service</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

CreateServiceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func,
  projectId: PropTypes.string,
  revisionId: PropTypes.string,
  tenantId: PropTypes.string
};

export default CreateServiceModal;
